import NotificationTreatmentHeader from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/Header'
import { NotificationTreatmentProgressBarNav } from 'pages/NotificationTreatment/ProgressBarNav'
import { calculateObjectProbabilityOcurrence } from 'pages/NotificationTreatment/helpers'
import tw from 'tailwind-styled-components'
import NotificationActions from 'actions/Notification'
import { useDispatch, useSelector } from 'react-redux'
import { notificationStatus } from 'constants/notificationStatus'
import { RootReducerState } from 'types/global'
import { NotificationReducer } from 'types/notification'
import { useEffect, useState, useMemo } from 'react'
import { AiFillQuestionCircle } from 'react-icons/ai'
import { MdPersonOutline } from 'react-icons/md'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import NotificationService from 'services/Notification'
import { IAuthenticationReducer } from 'types/autentication'
import { formatNotificationDate } from 'utils/dateUtils'
import {
    groupHasNotifiableReopenBlocked,
    isCircumstanceNotifiableActive,
    isNotifiableDamage,
    NOTIFIABLE_REOPEN_BLOCKED_MESSAGE
} from 'utils/damage'
import {
    groupHasLocationReopenBlocked,
    isLocationReopenBlocked,
    LOCATION_REOPEN_BLOCKED_MESSAGE
} from 'utils/location'
import { GroupRemoveNotificationsModal } from 'components/Modals/GroupRemoveNotifications'
import { GroupMoreInfoModal } from 'components/Modals/GroupMoreInfo'
import { FinishGroupTreatmentModal } from 'components/Modals/FinishGroupTreatment'
import { ReopenNotificationModal } from 'components/Modals/ReopenNotification'
import { PendingStepModal } from 'components/Modals/PendingStepModal'
import { roleToName } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { GroupActionPlan } from 'pages/NotificationTreatment/GroupTreatmentsSteps/GroupActionPlan'
import { GroupIshikawaDiagram } from 'pages/NotificationTreatment/GroupTreatmentsSteps/GroupIshikawaDiagram'
import { GroupLondonProtocol } from 'pages/NotificationTreatment/GroupTreatmentsSteps/GroupLondonProtocol'
import { GroupAccountabilityMatrix } from 'pages/NotificationTreatment/GroupTreatmentsSteps/GroupAccountabilityMatrix'
import { GroupFinishStep } from 'pages/NotificationTreatment/GroupTreatmentsSteps/GroupFinishStep'
import { listOfRoles, Role } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { isFinishedStatus } from 'pages/NotificationTreatment/NotificationTreatment'
import YellowCircleInterrogation from 'components/Icons/YellowCircleInterrogation'
import ActionStatus from 'components/ActionStatus'
import GroupAssumeTreatmentInfo from '../components/GroupAssumeTreatmentInfo'
import { GroupAssumeTreatmentModal } from 'components/Modals/GroupAssumeTreatmentModal'
import CanNotEditInfo from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/components/CanNotEditTreatmentInfo'

// Mesmas regras do NotifyContainer individual: sem w-full — 100% + margens horizontais estoura a viewport.
// Altura mínima para o card acompanhar a viewport até perto do rodapé, como no tratamento igual ao individual.
const GroupNotifyContainer = tw.div`
md:mx-12
mx-6
my-6
bg-white
boxShadow
rounded-lg
min-w-[380px]
relative
flex
flex-col
min-h-[calc(100vh-9rem)]
`

const GroupNotifyMain = tw.div`
flex
flex-col
flex-1
min-w-0
min-h-0
`

const GroupNotifyContent = tw.div`
w-full
min-w-0
mt-6
flex
flex-col
flex-1
gap-2
`

interface NotificationItem {
    number: { value: string; display_value: string }
    incident_title: { value: string; display_value: string }
    incident_description: { value: string; display_value: string }
    sys_created_on: { value: string; display_value: string }
    sys_id?: { value: string; display_value: string } // ID da notificação (se disponível)
    u_state?: { value: string; display_value: string }
    u_sub_state?: { value: string; display_value: string }
    damage_level?: { value: string; display_value: string }
    u_image_risk?: { value: string; display_value: string }
    u_never_events_or_sentinel_event?: { value: string; display_value: string }
    u_managers_review?: { value: string; display_value: string }
    u_treatment_group?: { value: string; display_value: string }
    u_disclosure?: { value: string; display_value: string }
    u_disclosure_comments?: { value: string; display_value: string }
    u_date_and_hour_notification_closed?: { value: string; display_value: string }
    u_assigned_to?: { value: string; display_value: string }
    u_reclassified_incident_type?: { value: string; display_value: string }
    location_incident?: { value: string; display_value: string }
    incident_sector?: { value: string; display_value: string }
    selected?: boolean
}

interface GroupData {
    group_key: string
    group_info: {
        u_notifica_user: { value: string; display_value: string }
        u_number: { value: string; display_value: string }
        u_related_notifications: { value: string; display_value: string }
        u_sector: { value: string; display_value: string }
        u_incident_type: { value: string; display_value: string }
        u_group_damage_rating: { value: string; display_value: string }
        u_location: { value: string; display_value: string }
        u_assigned_to: { value: string; display_value: string }
        sys_id: { value: string; display_value: string }
    }
    total_notifications: number
    notifications: NotificationItem[]
}

// Perfis permitidos para editar em tratamento em grupo
// corporate_quality_board NÃO pode editar notificações em grupo
export const enabledStepOneRoles = [
    listOfRoles.admin,
    listOfRoles.location_qa
]
export const canEditStepOneRoles = (loggedUserRole: Role) => {
    return enabledStepOneRoles.includes(loggedUserRole)
}

interface FormInputsGroupProps {
    statusIsPendingFinishOrFinished: boolean
    auth: IAuthenticationReducer
    canEditStepOne: boolean
    currentStepSelected: number
    setCurrentStepSelected: React.Dispatch<React.SetStateAction<number>>
    tooltipAlert: string
    setTooltipAlert: React.Dispatch<React.SetStateAction<string>>
}

const FormInputsGroupTreatment = ({
    statusIsPendingFinishOrFinished,
    auth,
    canEditStepOne,
    currentStepSelected,
    setCurrentStepSelected,
    tooltipAlert,
    setTooltipAlert,
}: FormInputsGroupProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { groupId: urlGroupId } = useParams()
    const dispatch = useDispatch()
    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const [selectedNotifications, setSelectedNotifications] = useState<string[]>([])
    const [finishedSteps, setFinishedSteps] = useState<isFinishedStatus[]>([])
    const [groupData, setGroupData] = useState<GroupData | null>(null)
    const [loading, setLoading] = useState(true)

    // Determinar o status real baseado nos dados do grupo
    const realStatusIsPendingFinishOrFinished = useMemo(() => {
        const firstNotification = groupData?.notifications?.[0]
        const uStateValue = firstNotification?.u_state?.value
        
        // Verificar se está em status finalizado ou pendente finalização
        const isFinalized = uStateValue === '5' // Finalizada
        const isPendingFinish = uStateValue === '4' // Pendente Finalização
        
        const result = isFinalized || isPendingFinish
        
        return result
    }, [groupData])

    const [showRemoveModal, setShowRemoveModal] = useState(false)
    const [isRemovingGroupNotifications, setIsRemovingGroupNotifications] = useState(false)
    const [showMoreInfoModal, setShowMoreInfoModal] = useState(false)
    const [showFinishModal, setShowFinishModal] = useState(false)
    const [showReopenModal, setShowReopenModal] = useState(false)
    const [isReopening, setIsReopening] = useState(false)
    const [showPendingStepsModal, setShowPendingStepsModal] = useState(false)
    const [showAssumeGroupModal, setShowAssumeGroupModal] = useState(false)
    const [isAssumingGroup, setIsAssumingGroup] = useState(false)
    const [isFinishingGroup, setIsFinishingGroup] = useState(false)
    const [isJustificationFilled, setIsJustificationFilled] = useState<boolean>(false)
    const [justificationValue, setJustificationValue] = useState('')
    const [isActionPlanCompleted, setIsActionPlanCompleted] = useState<boolean>(false)
    // Para tratamento em grupo, disclosure sempre será "sim"
    const [disclosureData, setDisclosureData] = useState<{ u_disclosure: string; u_disclosure_comments: string }>({
        u_disclosure: 'u_disclosure_yes',
        u_disclosure_comments: ''
    })

    // Verificar se o tratamento já foi iniciado (tem dados preenchidos)
    const isTreatmentStarted = useMemo(() => {
        // Verificar se tem dados de Ishikawa (com conteúdo real, não apenas estrutura)
        const hasIshikawaData = notificationSelector?.ishikawaDiagram && 
            Object.keys(notificationSelector.ishikawaDiagram).length > 0 &&
            // Verificar se o problem está preenchido
            notificationSelector.ishikawaDiagram.problem && 
            notificationSelector.ishikawaDiagram.problem.trim() !== '' &&
            // Verificar se as categorias têm conteúdo real
            notificationSelector.ishikawaDiagram.categories &&
            notificationSelector.ishikawaDiagram.categories.some(category => 
                category && category.causes && category.causes.length > 0
            )

        // Verificar se tem dados de Plano de Ação (com conteúdo real)
        const individualPlans = notificationSelector?.actionPlans || []
        const groupPlans = notificationSelector?.actionPlansGroup || []
        const actionPlans = individualPlans.length > 0 ? individualPlans : groupPlans
        const hasActionPlanData = actionPlans && actionPlans.length > 0 && 
            actionPlans.some(plan => plan.what && plan.what.trim() !== '')

        // Verificar se tem justificativa preenchida (a textarea real)
        const hasJustification = justificationValue && justificationValue.trim().length > 0

        // IGNORAR disclosure - sempre é 'u_disclosure_yes' por padrão
        // Só considerar se tem dados REAIS de tratamento
        return hasIshikawaData || hasActionPlanData || hasJustification
    }, [notificationSelector?.ishikawaDiagram, notificationSelector?.actionPlans, notificationSelector?.actionPlansGroup, justificationValue])

    // Função para verificar se todas as etapas obrigatórias foram concluídas
    const checkAllRequiredStepsCompleted = useMemo(() => {
        if (!groupData || !groupData.notifications || groupData.notifications.length === 0) {
            return false
        }

        const firstNotification = groupData.notifications[0]
        
        // 1. Verificar se a notificação não está finalizada/arquivada
        const uStateValue = firstNotification?.u_state?.value
        if (uStateValue === '5' || uStateValue === '6') {
            return false
        }

        // 2. Verificar se tem justificativa preenchida
        // Verificar tanto u_managers_review quanto u_disclosure_comments
        const hasJustification = justificationValue && justificationValue.trim().length > 0
        
        // Para tratamento em grupo, disclosure sempre é "sim", então só verificar se tem comentários
        const hasDisclosureComments = disclosureData.u_disclosure_comments && 
            disclosureData.u_disclosure_comments.trim().length > 0
        
        if (!hasJustification && !hasDisclosureComments) {
            return false
        }

        // 3. Verificar etapa obrigatória de Ishikawa
        const hasIshikawa = notificationSelector?.ishikawaDiagram?.categories?.length > 0
        if (!hasIshikawa) {
            return false
        }
        
        // Verificar se pelo menos uma categoria tem causas preenchidas
        const hasValidIshikawa = notificationSelector?.ishikawaDiagram?.categories?.some(category => 
            category.causes && category.causes.length > 0 && 
            category.causes.some(cause => cause && cause.trim() !== '')
        )
        
        if (!hasValidIshikawa) {
            return false
        }

        return true
    }, [
        groupData,
        justificationValue,
        disclosureData,
        notificationSelector?.ishikawaDiagram,
        currentStepSelected
    ])

    // Função para atualizar justificativa
    const handleJustificationChange = (isFilled: boolean, value: string = '') => {
        setJustificationValue(value)
    }

    // Função para atualizar o estado do plano de ação
    const handleActionPlanChange = (isCompleted: boolean) => {
        setIsActionPlanCompleted(isCompleted)
    }

    // Função para atualizar os dados do disclosure
    const handleDisclosureChange = (newDisclosureData: { u_disclosure: string; u_disclosure_comments: string }) => {
        setDisclosureData((prev) => {
            if (
                prev.u_disclosure === newDisclosureData.u_disclosure &&
                prev.u_disclosure_comments === newDisclosureData.u_disclosure_comments
            ) {
                return prev
            }
            return newDisclosureData
        })
    }

    // Dados reais da notificação escolhida
    const selectedNotification = location.state?.selectedNotification
    const currentUser = auth?.dataJwt
    // Prioriza o groupId da URL, senão pega do state da notificação
    const groupId = urlGroupId || selectedNotification?.u_treatment_group?.value

    // Verificar se o usuário logado é o responsável pelo grupo
    const loggedInUserIsResponsible = useMemo(() => {
        if (!groupData?.notifications?.[0] || !currentUser?.sys_id) {
            return false
        }
        
        return currentUser.sys_id === groupData.notifications[0].u_assigned_to?.value
    }, [groupData, currentUser])

    const canReopenGroupTreatment = useMemo(() => {
        if (!groupData?.notifications?.length) {
            return false
        }

        if (isCircumstanceNotifiableActive()) {
            return true
        }

        return groupData.notifications.some(
            notification =>
                (isCircumstanceNotifiableActive() ||
                    !isNotifiableDamage(notification.damage_level?.value)) &&
                !isLocationReopenBlocked(notification.location_incident)
        )
    }, [groupData])

    const groupHasNotifiableCircumstance = useMemo(
        () =>
            groupData?.notifications?.some(notification =>
                isNotifiableDamage(notification.damage_level?.value)
            ) ?? false,
        [groupData]
    )

    const hasLocationReopenBlocked = useMemo(
        () => groupHasLocationReopenBlocked(groupData?.notifications),
        [groupData]
    )

    const hasNotifiableReopenBlocked = useMemo(
        () => groupHasNotifiableReopenBlocked(groupData?.notifications),
        [groupData]
    )

    // Buscar dados do grupo e notificações
    useEffect(() => {
        const fetchGroupData = async () => {
            if (!groupId) {
                console.error('ID do grupo não encontrado')
                setLoading(false)
                return
            }

            try {
                setLoading(true)

                // Buscar dados completos do grupo incluindo notificações
                const groupResponse = await NotificationService.getGroupDataById(groupId)

                // Verificar se group_info existe e não é null
                if (!groupResponse?.group_info) {
                    console.warn('Grupo não encontrado ou group_info vazio. Redirecionando para tela de notificações.')
                    navigate('/notificacoes')
                    return
                }

                setGroupData(groupResponse)
            } catch (error) {
                console.error('Erro ao buscar dados do grupo:', error)
                navigate('/notificacoes')
            } finally {
                setLoading(false)
            }
        }

        fetchGroupData()
    }, [groupId])

    // Carregar justificativa existente quando os dados do grupo são carregados
    useEffect(() => {
        if (groupData?.notifications?.[0]) {
            const firstNotification = groupData.notifications[0]
            const existingJustification = firstNotification?.u_managers_review?.display_value || ''
            
            setJustificationValue(existingJustification)
            
            // Se estiver finalizada, ir para etapa 4
            if (firstNotification?.u_state?.value === '5') {
                setCurrentStepSelected(6)
            }
        } 
    }, [groupData])

    // Atualizar estado do botão quando as condições mudarem
    useEffect(() => {
        const booleanValue = Boolean(checkAllRequiredStepsCompleted)
        setIsJustificationFilled(booleanValue)
    }, [checkAllRequiredStepsCompleted])

    // FORÇA a atualização quando a justificativa mudar diretamente
    useEffect(() => {
        const hasText = justificationValue && justificationValue.trim().length > 0
        setIsJustificationFilled(hasText)
    }, [justificationValue])

    // Carregar dados do Ishikawa e Action Plans da primeira notificação para atualizar a barra de progresso
    useEffect(() => {
        if (groupData?.notifications?.length > 0) {
            const firstNotificationId = groupData.notifications[0]?.sys_id?.value
            if (firstNotificationId) {
                // Carregar Ishikawa da primeira notificação
                NotificationActions.getIshikawaByNotificationId(dispatch, firstNotificationId)
                // Carregar Protocolo de Londres da primeira notificação
                NotificationActions.getLondonProtocolByNotificationId(dispatch, firstNotificationId)
                // Carregar Matriz de responsabilização da primeira notificação
                NotificationActions.getAccountabilityMatrixByNotificationId(dispatch, firstNotificationId)
                // Carregar Action Plans da primeira notificação
                NotificationActions.getListOfActionPlan(dispatch, firstNotificationId)
            }
        } else if (groupId) {
            // Fallback: tentar carregar pelo groupId
            NotificationActions.getListOfActionPlanGroup(dispatch, groupId)
        }
    }, [groupData, groupId, dispatch])

    // Atualizar URL se necessário (quando chega via state mas não tem groupId na URL)
    useEffect(() => {
        if (selectedNotification?.u_treatment_group?.value && !urlGroupId) {
            const groupIdFromState = selectedNotification.u_treatment_group.value
            navigate(`/notificacoes/group-treatment/${groupIdFromState}`, {
                state: { selectedNotification },
                replace: true
            })
        }
    }, [selectedNotification, urlGroupId, navigate])


    // Calcular steps baseado nas regras de negócio
    useEffect(() => {
        const firstNotification = groupData?.notifications?.[0]
        const isFinalized = firstNotification?.u_state?.value === '5'
        const finalizationDate = firstNotification?.u_date_and_hour_notification_closed?.display_value
        
        // Para grupo, sempre mostrar apenas os steps necessários
        const groupFinishedSteps: isFinishedStatus[] = [
            {
                label: 'Dados da notificação',
                isRequired: true,
                isOptional: false,
                isOlder: false,
                currentStepTreatment: 1,
                isDone: false,
                isDispensable: false
            },
            {
                label: 'Análise de Ishikawa',
                isRequired: true,
                isOptional: false,
                isOlder: false,
                currentStepTreatment: 2,
                isDone: false,
                isDispensable: false
            },
            {
                label: 'Protocolo de Londres',
                isRequired: false,
                isOptional: true,
                isOlder: false,
                currentStepTreatment: 3,
                isDone: false,
                isDispensable: false
            },
            {
                label: 'Matriz de responsabilização',
                isRequired: false,
                isOptional: true,
                isOlder: false,
                currentStepTreatment: 4,
                isDone: false,
                isDispensable: false
            },
            {
                label: 'Plano de ação',
                isRequired: false,
                isOptional: true,
                isOlder: false,
                currentStepTreatment: 5,
                isDone: false,
                isDispensable: false
            },
            {
                label: isFinalized && finalizationDate 
                    ? `Finalizada em ${finalizationDate}` 
                    : 'Finalizar notificação',
                isRequired: true,
                isOptional: false,
                isOlder: false,
                currentStepTreatment: 6,
                isDone: false,
                isDispensable: false
            }
        ]
        setFinishedSteps(groupFinishedSteps)
    }, [groupData])

    const handleBack = () => {
        navigate('/notificacoes')
    }

    const handleNavigateSteps = ({ type }: { type: 'forward' | 'finish' }) => {
        if (type === 'forward') {
            if (currentStepSelected < 6) {
                setCurrentStepSelected(currentStepSelected + 1)
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        }
    }

    const handleBackStep = () => {
        if (currentStepSelected > 1) {
            setCurrentStepSelected(currentStepSelected - 1)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleSelectNotification = (notificationSysId: string) => {
        setSelectedNotifications(prev =>
            prev.includes(notificationSysId)
                ? prev.filter(id => id !== notificationSysId)
                : [...prev, notificationSysId]
        )
    }

    const handleSelectAll = () => {
        if (!groupData?.notifications) return

        const validNotifications = groupData.notifications.filter(n => n.sys_id?.value)

        if (selectedNotifications.length === validNotifications.length) {
            setSelectedNotifications([])
        } else {
            setSelectedNotifications(validNotifications.map(n => n.sys_id?.value).filter(id => id) || [])
        }
    }

    // Determinar o status baseado no estado real das notificações
    const getGroupStatusState = () => {
        const firstNotification = groupData?.notifications?.[0]
        const uStateValue = firstNotification?.u_state?.value
        
        if (realStatusIsPendingFinishOrFinished) {
            const result = uStateValue === '5' ? 'Finalizada' : 'Pendente Finalização'
            return result
        }
        
        // Retornar o status real da primeira notificação usando mapeamento centralizado
        if (uStateValue) {
            const status = notificationStatus.find(status => status.value === uStateValue)
            const result = status?.label || 'Em Tratamento'
            return result
        }
        
        return 'Em Tratamento'
    }

    const handleViewData = (notificationId: string) => {
        if (!notificationId) {
            console.error('❌ ID da notificação não encontrado')
            return
        }

        // Coletar todos os sys_ids das notificações do grupo
        const notificationIds = groupData?.notifications
            ?.map(notification => notification.sys_id?.value)
            .filter(id => id) || []
        navigate(`/notificacoes/view-group`, {
            state: {
                fromGroup: true,
                groupId: groupId,
                notificationIds: notificationIds,
                initialNotificationId: notificationId
            }
        })
    }

    const handleRemoveNotifications = () => {
        setShowRemoveModal(true)
    }

    const handleConfirmRemoval = async () => {
        try {
            setIsRemovingGroupNotifications(true)
            // Obter os sys_id das notificações selecionadas
            const selectedNotificationIds = selectedNotifications

            if (selectedNotificationIds.length === 0) {
                console.error('❌ Nenhuma notificação válida selecionada para remoção - sys_id não encontrado')
                return
            }

            // Calcular quantas notificações restarão após a remoção
            const totalNotifications = groupData?.total_notifications || 0
            const notificationsToRemove = selectedNotificationIds.length
            const remainingNotifications = totalNotifications - notificationsToRemove

            // Se restar apenas 1 notificação, incluir ela na remoção para deletar o grupo
            let finalNotificationIds = [...selectedNotificationIds]
            
            if (remainingNotifications === 1) {
                // Encontrar a notificação que restará
                const remainingNotification = groupData?.notifications?.find(
                    notification => !selectedNotificationIds.includes(notification.sys_id?.value || '')
                )
                
                if (remainingNotification?.sys_id?.value) {
                    finalNotificationIds.push(remainingNotification.sys_id.value)
                }
            }

            // Determinar o status alvo baseado no tratamento
            const targetStatus = isTreatmentStarted ? '3' : '2' // Em Tratamento ou Aguardando
            
            // Chamar o endpoint para remover as notificações do grupo
            const response = await NotificationService.removeFromTreatmentGroup(finalNotificationIds, targetStatus)

            // Se removemos todas as notificações (grupo deletado), redirecionar
            if (finalNotificationIds.length === totalNotifications) {
                navigate('/notificacoes')
                return
            }

            // Atualizar a lista local apenas se o grupo não foi deletado
            if (groupData?.notifications) {
                const updatedNotifications = groupData.notifications.filter(
                    notification => !finalNotificationIds.includes(notification.sys_id?.value || '')
                )
                setGroupData({
                    ...groupData,
                    notifications: updatedNotifications,
                    total_notifications: updatedNotifications.length
                })
                setSelectedNotifications([])
            }
        } catch (error) {
            console.error('Erro ao remover notificações do grupo:', error)
            // TODO: Mostrar toast de erro para o usuário
        } finally {
            setIsRemovingGroupNotifications(false)
            setShowRemoveModal(false)
        }
    }

    const handleCloseRemoveModal = () => {
        setShowRemoveModal(false)
    }

    const handleOpenMoreInfoModal = () => {
        setShowMoreInfoModal(true)
    }

    const handleCloseMoreInfoModal = () => {
        setShowMoreInfoModal(false)
    }

    const handleOpenFinishModal = () => {
        if (!checkAllRequiredStepsCompleted) {
            setShowPendingStepsModal(true)
            return
        }
        
        setShowFinishModal(true)
    }

    const handleCloseFinishModal = () => {
        setShowFinishModal(false)
    }

    const handleClosePendingStepsModal = () => {
        setShowPendingStepsModal(false)
    }

    // Função para gerar lista de etapas pendentes no formato do modal
    const getPendingStepsList = () => {
        const steps = []
        
        if (!groupData || !groupData.notifications || groupData.notifications.length === 0) {
            return steps
        }
        
        // 1. PRIMEIRO: Análise de Ishikawa - OBRIGATÓRIA em grupos
        const hasIshikawa = notificationSelector?.ishikawaDiagram?.categories?.length > 0
        const hasValidIshikawa = notificationSelector?.ishikawaDiagram?.categories?.some(category => 
            category.causes && category.causes.length > 0 && 
            category.causes.some(cause => cause && cause.trim() !== '')
        )
        
        steps.push({
            label: 'Análise de Ishikawa',
            isDone: hasIshikawa && hasValidIshikawa,
            isRequired: true,
            isOptional: false,
            isDispensable: false,
            currentStepTreatment: 2
        })
        
        // 2. SEGUNDO: Protocolo de Londres
        const londonIsDone =
            !!notificationSelector?.londonProtocol?.date_of_patient_admission ||
            notificationSelector?.londonProtocol?.do_not_perform_optional === 'true'
        steps.push({
            label: 'Protocolo de Londres',
            isDone: londonIsDone,
            isRequired: false,
            isOptional: true,
            isDispensable: false,
            currentStepTreatment: 3
        })

        // 3. TERCEIRO: Matriz de responsabilização
        const matrixIsDone =
            !!notificationSelector?.accountabilityMatrix?.[0] &&
            (notificationSelector?.accountabilityMatrix?.[0]?.u_do_not_perform_optional === 'true' ||
                notificationSelector?.accountabilityMatrix?.[0]?.u_matrix_result !== '')
        steps.push({
            label: 'Matriz de responsabilização',
            isDone: matrixIsDone,
            isRequired: false,
            isOptional: true,
            isDispensable: false,
            currentStepTreatment: 4
        })

        // 4. QUARTO: Plano de ação — mesma regra da barra de progresso (individual ou grupo)
        const individualActionPlans = notificationSelector?.actionPlans || []
        const actionPlansGroup = notificationSelector?.actionPlansGroup || []
        const hasIndividualPlans =
            individualActionPlans.length > 0 &&
            individualActionPlans[0]?.sys_id &&
            (individualActionPlans[0]?.u_do_not_perform_optional === 'true' ||
                individualActionPlans[0]?.what !== '')
        const hasGroupPlans =
            actionPlansGroup.length > 0 &&
            actionPlansGroup[0]?.sys_id &&
            (actionPlansGroup[0]?.u_do_not_perform_optional === 'true' ||
                actionPlansGroup[0]?.what !== '')
        const hasValidActionPlan = hasIndividualPlans || hasGroupPlans

        steps.push({
            label: 'Plano de ação',
            isDone: hasValidActionPlan,
            isRequired: false,
            isOptional: true,
            isDispensable: false,
            currentStepTreatment: 5
        })
        
        // 5. QUINTO: Finalização - SEMPRE OBRIGATÓRIA em grupos
        const hasJustification = justificationValue && justificationValue.trim().length > 0
        const hasDisclosureComments = disclosureData.u_disclosure_comments && 
            disclosureData.u_disclosure_comments.trim().length > 0
        
        steps.push({
            label: 'Finalização',
            isDone: hasJustification || hasDisclosureComments,
            isRequired: true,
            isOptional: false,
            isDispensable: false,
            currentStepTreatment: 6
        })
        
        return steps
    }

    const handleConfirmFinish = async () => {
        try {
            setIsFinishingGroup(true)
            if (!groupData || !groupData.notifications || groupData.notifications.length === 0) {
                setTooltipAlert('Erro: Dados do grupo não encontrados')
                setTimeout(() => {
                    setTooltipAlert('')
                }, 4000)
                return
            }

            if (!checkAllRequiredStepsCompleted) {
                // Mostrar modal de etapas pendentes ao invés de bloquear
                setShowPendingStepsModal(true)
                return
            }
            
            // Preparar lista de notificações para finalizar
            const notificationsToFinish = groupData?.notifications.map(notification => ({
                sys_id: notification.sys_id.value,
                value: notification.sys_id.value
            })) || []

            if (notificationsToFinish.length === 0) {
                throw new Error('Nenhuma notificação encontrada para finalizar')
            }

            // Determinar a justificativa a enviar: prioriza o que foi digitado no campo de disclosure (quando exibido)
            const unifiedJustification = (disclosureData.u_disclosure_comments?.trim() || justificationValue || '').trim()

            const finalDisclosureData = {
                u_managers_review: unifiedJustification,
                u_disclosure: disclosureData.u_disclosure,
                u_disclosure_comments: unifiedJustification
            }


            // Chamar endpoint de finalização em lote
            const result = await NotificationService.finishGroupTreatment(
                notificationsToFinish,
                currentUser?.sys_id || '',
                finalDisclosureData
            )

            if (result.success) {
                // Mostrar mensagem de sucesso igual à finalização individual
                setTooltipAlert('Tratamento em grupo finalizado com sucesso!')
                
                // Ir para etapa de finalização (modo visualização)
                setCurrentStepSelected(6)
                
                // Recarregar dados do grupo para refletir estado finalizado sem reload
                try {
                    const refreshed = await NotificationService.getGroupDataById(groupId!)
                    setGroupData(refreshed)
                } catch {}
                
                // Limpar mensagem após 4 segundos
                setTimeout(() => {
                    setTooltipAlert('')
                }, 4000)
            } else {
                // Mostrar detalhes dos erros
                result.error_details?.forEach((erro: any) => {
                    console.error(`Erro no índice ${erro.index}:`, erro.error)
                })

                // Mesmo com erros parciais, redirecionar mostrando o resultado
                navigate('/notificacoes', {
                    state: {
                        messageSuccess: `Grupo finalizado com avisos: ${result.updated} notificação(ões) finalizada(s), ${result.errors} com erro.`
                    }
                })
            }
        } catch (error) {
            console.error('❌ Erro ao finalizar grupo:', error)
            throw error
        } finally {
            setShowFinishModal(false)
            setIsFinishingGroup(false)
        }
    }

    // Funções para reabertura
    const handleReopenNotification = () => {
        setShowReopenModal(true)
    }

    const handleCloseReopenModal = () => {
        setShowReopenModal(false)
    }

    // Funções para assumir tratamento
    const handleOpenAssumeGroupModal = () => {
        setShowAssumeGroupModal(true)
    }

    const handleCloseAssumeGroupModal = () => {
        setShowAssumeGroupModal(false)
    }

    const handleConfirmAssumeGroup = async () => {
        try {
            setIsAssumingGroup(true)

            if (!groupData?.notifications?.length || !currentUser?.sys_id) {
                console.error('Dados insuficientes para assumir tratamento')
                return
            }

            const notificationIds = (groupData.notifications
                .map(notification => notification.sys_id?.value)
                .filter(id => id) || []) as string[]

            if (notificationIds.length === 0) {
                console.error('Nenhuma notificação válida encontrada')
                return
            }

            const result = await NotificationService.assumirGroupTreatment(
                notificationIds,
                currentUser.sys_id
            )

            if (result) {
                setTooltipAlert('Tratamento do grupo assumido com sucesso!')
                try {
                    const refreshed = await NotificationService.getGroupDataById(groupId!)
                    setGroupData(refreshed)
                } catch {}
                setTimeout(() => setTooltipAlert(''), 4000)
            }
        } catch (error) {
            setTooltipAlert('Erro ao assumir tratamento do grupo')
            setTimeout(() => setTooltipAlert(''), 4000)
        } finally {
            setIsAssumingGroup(false)
            setShowAssumeGroupModal(false)
        }
    }

    const handleConfirmReopen = async () => {
        try {
            setIsReopening(true)
            const notificationIds = groupData?.notifications
                .filter(notification =>
                    (isCircumstanceNotifiableActive() ||
                        !isNotifiableDamage(notification.damage_level?.value)) &&
                    !isLocationReopenBlocked(notification.location_incident)
                )
                .map(notification => notification.sys_id.value) || []

            if (notificationIds.length === 0) {
                setShowReopenModal(false)
                const blockedMessage = groupHasNotifiableReopenBlocked(groupData?.notifications)
                    ? NOTIFIABLE_REOPEN_BLOCKED_MESSAGE
                    : LOCATION_REOPEN_BLOCKED_MESSAGE
                setTooltipAlert(blockedMessage)
                setTimeout(() => setTooltipAlert(''), 4000)
                return
            }

            // Chamar endpoint de reabertura em lote
            const result = await NotificationService.reopenGroupNotification(
                notificationIds,
                currentUser?.mail || ''
            )

            if (result) {
                // Resetar estados relacionados à finalização
                setIsJustificationFilled(false)
                setJustificationValue('')
                setDisclosureData({
                    u_disclosure: 'u_disclosure_yes',
                    u_disclosure_comments: ''
                })
                
                // Navegar para a primeira etapa
                setCurrentStepSelected(1)
                
                // Fechar modal
                setShowReopenModal(false)
                
                // Recarregar a página para atualizar os dados
                window.location.reload()
            }
        } catch (error) {
            console.error('❌ Erro ao reabrir grupo:', error)
            // Fechar modal mesmo com erro
            setShowReopenModal(false)
        }
        finally {
            setIsReopening(false)
        }
    }

    const renderCurrentStep = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="text-gray-600">Carregando dados do grupo...</div>
                </div>
            )
        }

        switch (currentStepSelected) {
            case 1:
                return (
                    <div data-testid="test-notification-treatment-group" className='px-[33px]'>
                        {/* Dados da notificação */}
                        <div className="grid grid-cols-4 gap-5">
                            <div className="col-span-1">
                                <span className="text-xs text-darkGray80">Responsável:</span>
                                <div className="text-sm text-roxo font-semibold flex items-center gap-x-2">
                                    <MdPersonOutline className="w-5 h-5 text-roxo" />
                                    {groupData?.notifications?.[0]?.u_assigned_to?.display_value || 'Não informado'}
                                </div>
                            </div>
                            <div className="col-span-1">
                                <span className="text-xs text-darkGray80">Unidade:</span>
                                <div className="text-sm text-darkGray font-semibold">
                                    {groupData?.group_info?.u_location?.display_value || 'Não informado'}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <span className="text-xs text-darkGray80">Setor:</span>
                                <div className="text-sm text-darkGray font-semibold">
                                    {groupData?.group_info?.u_sector?.display_value || 'Não informado'}
                                </div>
                            </div>
                            <div className="col-span-1">
                                <span className="text-xs text-darkGray80">Chave do grupo:</span>
                                <div className="text-sm text-darkGray font-semibold">
                                    {groupData?.group_info?.u_number?.display_value || 'Não informado'}
                                </div>
                            </div>
                            <div className="col-span-1">
                                <span className="text-xs text-darkGray80">Grau de dano do grupo:</span>
                                <div className="text-sm text-darkGray font-semibold">
                                    {groupData?.group_info?.u_group_damage_rating?.display_value || 'Não informado'}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <span className="text-xs text-darkGray80">Tipo do incidente do grupo:</span>
                                <div className="text-sm text-darkGray font-semibold">
                                    {groupData?.group_info?.u_incident_type?.display_value || 'Não informado'}
                                </div>
                            </div>
                        </div>
                        <hr className={`my-6 border-[1px] border-[#EBEDF2]`} />
                        {/* Notificações do grupo */}
                        <div className="">
                            <div className="flex items-center space-x-4 mb-6">
                                <h2 className="text-lg font-semibold text-blueTheme">Notificações do grupo</h2>
                                <span className="bg-[#D9D9D9] text-darkGray px-2 py-1 rounded-full text-[13px]">
                                    {groupData?.total_notifications || 0} notificações
                                </span>
                                <button
                                    type="button"
                                    onClick={handleOpenMoreInfoModal}
                                    className="flex items-center space-x-2 text-blueTheme cursor-pointer"
                                >
                                    <YellowCircleInterrogation />
                                    <span className="text-sm font-semibold underline">Saiba mais</span>
                                </button>
                            </div>

                            <div className="overflow-x-auto rounded-t-lg">
                                <table className="w-full outline outline-2 outline-[#E0E0E0] rounded-lg border">
                                    <thead>
                                        <tr className="bg-[#EEEEF4] divide-x-2 divide-[#E0E0E0]">
                                            {!realStatusIsPendingFinishOrFinished && canEditStepOne && loggedInUserIsResponsible && (
                                                <th className="text-center py-[10.5px]">
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedNotifications.length === (groupData?.notifications?.filter(n => n.sys_id?.value).length || 0) &&
                                                            (groupData?.notifications?.filter(n => n.sys_id?.value).length || 0) > 0
                                                        }
                                                        onChange={handleSelectAll}
                                                        className="w-4 h-4 text-blueTheme border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                </th>
                                            )}

                                            <th className="text-center py-[10.5px] text-[13px] font-semibold text-darkGray">
                                                Título do incidente
                                            </th>
                                            <th className="text-center py-[10.5px] text-[13px] font-semibold text-darkGray">
                                                Descrição do incidente
                                            </th>
                                            <th className="text-center py-[10.5px] text-[13px] font-semibold text-darkGray">
                                                Data da notificação
                                            </th>
                                            <th className="text-center py-[10.5px] text-[13px] font-semibold text-darkGray">
                                                Chave
                                            </th>
                                            <th className="text-center py-[10.5px] text-[13px] font-semibold text-darkGray">
                                                Ação
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-[#E0E0E0]">
                                        {!groupData?.notifications || groupData.notifications.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="py-8 px-4 text-center text-darkGray80">
                                                    Nenhuma notificação encontrada para este grupo
                                                </td>
                                            </tr>
                                        ) : (
                                            groupData.notifications.map((notification, index) => (
                                                <tr key={notification.sys_id?.value || notification.number.value || index} className="hover:bg-gray-50 divide-x-2 divide-[#E0E0E0]">
                                                    {!realStatusIsPendingFinishOrFinished && canEditStepOne && loggedInUserIsResponsible && (
                                                    <td className="text-center px-2 py-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedNotifications.includes(notification.sys_id?.value || '')}
                                                            onChange={() => handleSelectNotification(notification.sys_id?.value || '')}
                                                            className="w-4 h-4 text-blueTheme border-gray-300 rounded focus:ring-blue-500"
                                                            disabled={!notification.sys_id?.value}
                                                        />
                                                    </td>
                                                    )}
                                                    <td className="py-[6px] px-4 text-xs text-textSelect max-w-xs">
                                                        {notification.incident_title?.display_value || 'Sem título'}
                                                    </td>
                                                    <td className="py-[6px] px-4 text-xs text-textSelect max-w-xs">
                                                        {notification.incident_description?.display_value || 'Sem descrição'}
                                                    </td>
                                                    <td className="py-[6px] px-4 text-xs text-textSelect">
                                                        {formatNotificationDate(notification.sys_created_on?.value)}
                                                    </td>
                                                    <td className="py-[6px] px-4 text-xs text-center text-textSelect">
                                                        {notification.number?.display_value || 'Não informado'}
                                                    </td>
                                                    <td className="py-[6px] px-4 text-center">
                                                        <button
                                                            onClick={() => handleViewData(notification.sys_id?.value || notification.number.value)}
                                                            className="text-blueTheme hover:text-blue-700 text-xs font-semibold underline"
                                                        >
                                                            Ver dados
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            case 2:
                return (
                    <GroupIshikawaDiagram
                        id={groupId || ''}
                        currentUser={currentUser}
                        tooltipAlert={tooltipAlert}
                        notificationStatusState={getGroupStatusState()}
                        notificationById={selectedNotification}
                        statusIsPendingFinishOrFinished={realStatusIsPendingFinishOrFinished}
                        groupData={groupData}
                        loggedInUserIsResponsible={loggedInUserIsResponsible}
                    />
                )
            case 3:
                return (
                    <GroupLondonProtocol
                        id={groupId || ''}
                        currentUser={currentUser}
                        tooltipAlert={tooltipAlert}
                        notificationStatusState={getGroupStatusState()}
                        notificationById={selectedNotification}
                        statusIsArchivedOrFinished={realStatusIsPendingFinishOrFinished}
                        groupData={groupData}
                        loggedInUserIsResponsible={loggedInUserIsResponsible}
                    />
                )
            case 4:
                return (
                    <GroupAccountabilityMatrix
                        id={groupId || ''}
                        currentUser={currentUser}
                        tooltipAlert={tooltipAlert}
                        notificationStatusState={getGroupStatusState()}
                        notificationById={selectedNotification}
                        statusIsArchivedOrFinished={realStatusIsPendingFinishOrFinished}
                        groupData={groupData}
                        loggedInUserIsResponsible={loggedInUserIsResponsible}
                    />
                )
            case 5:
                return (
                    <GroupActionPlan
                        id={groupId || ''}
                        currentUser={currentUser}
                        tooltipAlert={tooltipAlert}
                        notificationStatusState={getGroupStatusState()}
                        notificationById={selectedNotification}
                        statusIsArchivedOrFinished={realStatusIsPendingFinishOrFinished}
                        groupData={groupData}
                        onActionPlanChange={handleActionPlanChange}
                        loggedInUserIsResponsible={loggedInUserIsResponsible}
                    />
                )
            case 6:
                return (
                    <GroupFinishStep
                        id={groupId || ''}
                        currentUser={currentUser}
                        tooltipAlert={tooltipAlert}
                        notificationStatusState={getGroupStatusState()}
                        statusIsPendingFinishOrFinished={realStatusIsPendingFinishOrFinished}
                        groupData={groupData}
                        onJustificationChange={handleJustificationChange}
                        onDisclosureChange={handleDisclosureChange}
                        loggedInUserIsResponsible={loggedInUserIsResponsible}
                    />
                )
            default:
                return null
        }
    }

    return (
        <>
            <GroupNotifyContainer>
                <GroupNotifyMain>
                    {tooltipAlert && (
                        <div className="relative">
                            <div className="absolute left-1/2 -translate-x-1/2 -top-5 w-11/12 z-10">
                                <ActionStatus text={tooltipAlert} status={1} />
                            </div>
                        </div>
                    )}
                    
                    {/* Alerta para assumir tratamento em grupo */}
                    <GroupAssumeTreatmentInfo
                        currentUser={currentUser}
                        groupData={groupData}
                        handleAssumeGroupTreatment={handleOpenAssumeGroupModal}
                    />
                    
                    {/* Banner de permissão - usando a primeira notificação do grupo como referência */}
                    {groupData?.notifications?.[0] && currentUser && (
                        <div className="fixed top-[88px] w-full flex justify-center z-[99999]">
                            <CanNotEditInfo
                                notificationStatusState={getGroupStatusState()}
                                currentUser={currentUser}
                                notificationSector={groupData.notifications[0]?.incident_sector?.value || ''}
                                notificationLocation={groupData.notifications[0]?.location_incident?.value || ''}
                                notificationById={groupData.notifications[0]}
                            />
                        </div>
                    )}
                    
                    <GroupNotifyContent>
                        <NotificationTreatmentHeader
                            handleCancel={handleBack}
                            handleSaveEdit={() => { }}
                            showActionsButtonStep1={false}
                            forwardToFinish={handleNavigateSteps}
                            handleFinishNotification={handleBackStep}
                            currentUser={currentUser}
                            notificationStatusState={getGroupStatusState()}
                            isDirty={false}
                            currentStepSelected={currentStepSelected}
                            setCurrentStepSelected={setCurrentStepSelected}
                            handleArchiveNotification={() => { }}
                            handleOpenForwardNotification={() => { }}
                            handleAssumeNotification={() => { }}
                            isGroupTreatment={true}
                            hasSelectedGroupNotifications={selectedNotifications.length > 0}
                            handleRemoveGroupNotifications={handleRemoveNotifications}
                            selectedGroupNotificationsCount={selectedNotifications.length}
                            handleFinishGroupNotification={handleOpenFinishModal}
                            isJustificationFilled={isJustificationFilled}
                            loggedInUserIsResponsible={loggedInUserIsResponsible}
                            handleReopenNotification={handleReopenNotification}
                            canReopen={canReopenGroupTreatment}
                            isNotifiableCircumstance={groupHasNotifiableCircumstance}
                            hasNotifiableReopenBlocked={hasNotifiableReopenBlocked}
                            hasLocationReopenBlocked={hasLocationReopenBlocked}
                        />
                        <NotificationTreatmentProgressBarNav
                            notificationStatusState={getGroupStatusState()}
                            setCurrentStepSelected={setCurrentStepSelected}
                            currentStepSelected={currentStepSelected}
                            finishedSteps={finishedSteps}
                            isGroupTreatment={true}
                        />
                        <form className="pb-6 pt-2">
                            {renderCurrentStep()}
                        </form>
                    </GroupNotifyContent>
                </GroupNotifyMain>
            </GroupNotifyContainer>

            <GroupRemoveNotificationsModal
                isOpen={showRemoveModal}
                handleCloseModal={handleCloseRemoveModal}
                handleConfirmRemoval={handleConfirmRemoval}
                treatmentStatus={isTreatmentStarted ? "started" : "not_started"}
                selectedCount={selectedNotifications.length}
                isLoading={isRemovingGroupNotifications}
            />

            <GroupMoreInfoModal
                isOpen={showMoreInfoModal}
                handleCloseModal={handleCloseMoreInfoModal}
            />

            <FinishGroupTreatmentModal
                isOpen={showFinishModal}
                handleCloseModal={handleCloseFinishModal}
                handleConfirmFinish={handleConfirmFinish}
                totalNotifications={groupData?.total_notifications || 0}
                groupKey={groupData?.group_info?.u_number?.display_value || ''}
                groupIncidentType={groupData?.group_info?.u_incident_type?.display_value}
                groupLocation={groupData?.group_info?.u_location?.display_value}
                groupSector={groupData?.group_info?.u_sector?.display_value}
                groupDate={formatNotificationDate(groupData?.notifications[0]?.sys_created_on?.value)}
                currentUserName={currentUser?.name || 'Usuário'}
                currentUserProfile={roleToName[currentUser?.profile as keyof typeof roleToName] || 'Perfil não informado'}
                groupDamageRating={groupData?.group_info?.u_group_damage_rating?.value}
            />

            <ReopenNotificationModal
                isOpen={showReopenModal}
                handleCloseModal={handleCloseReopenModal}
                handleReopenNotification={handleConfirmReopen}
                isGroupTreatment={true}
                isLoading={isReopening}
            />

            <GroupAssumeTreatmentModal
                key={`assume-modal-${isAssumingGroup}`}
                isOpen={showAssumeGroupModal}
                handleCloseModal={handleCloseAssumeGroupModal}
                handleConfirmAssume={handleConfirmAssumeGroup}
                currentUser={currentUser}
                groupData={groupData}
                isLoading={isAssumingGroup}
            />

            <PendingStepModal
                isOpen={showPendingStepsModal}
                handleCloseModal={handleClosePendingStepsModal}
                handleGoToStep={() => {
                    const pendingSteps = getPendingStepsList()
                    const firstPendingStep = pendingSteps.find(step => !step.isDone)
                    if (firstPendingStep) {
                        setCurrentStepSelected(firstPendingStep.currentStepTreatment)
                    }
                    setShowPendingStepsModal(false)
                }}
                isFinishedList={getPendingStepsList()}
                subtitle="Para finalizar um tratamento em grupo, certifique-se de que todos os dados obrigatórios foram preenchidos."
            />
        </>
    )
}

export default FormInputsGroupTreatment 
